import React from 'react'
import { Box, Flex, Text, Button, Image, Heading } from 'pcln-design-system'
import styled from 'styled-components'
import { Carousel } from 'pcln-carousel'
import { MediaImageCard } from '@pcln/cards'
import { themeGet } from '@styled-system/theme-get'
import analytics from '@/shared-utils/analytics'
import LayoutComponentWrapper from '@/components/LayoutComponentWrapper'
import useDetectBrowser from '@/hooks/useDetectBrowser'
import { IMediaSnippet } from '@pcln/contentful-components'
import MediaSnippet from './MediaSnippet'

type MediaImageCard = {
  contentTypeId?: string
  backgroundImageUrl?: string
  callToAction?: {
    contentTypeId: string
    openInNewTab?: boolean
    url: string
    buttonText?: string
    label: string
    onClick?: (evt: MouseEvent) => void
    ariaLabel: string
  }
  contentAlign: 'default' | 'center'
  logoImageUrl?: string
  logoImageAlt?: string
  showBorder: boolean
  showLogoImageBackground?: boolean
  fixedLogoSize?: 'sm' | 'md' | 'lg'
  badgeColor?: string
  badgeText?: string
  borderText?: string
  heading?: string
  subheading?: string
  isCondensed?: boolean
  backgroundColor?: string
}

type SpotlightCollection = {
  primaryLogoImageUrl?: string
  primaryHeading?: string
  primaryHeadingColor?: string
  primarySubheading?: string
  primarySubheadingColor?: string
  primaryLogoImageAlt?: string
  backgroundColor?: string
  spotlightColor?: string
  callToAction?: {
    buttonText?: string
    url?: string
    googleAnalyticsTracking?: {
      label?: string
      action?: string
      category?: string
    }
    color?: string
  }
  mediaCards: Array<MediaImageCard | IMediaSnippet>
}

const StyledFlex = styled(Flex)`
  position: relative;
  gap: 16px;
`

const CarouselWrapper = styled(Box)`
  ${themeGet('mediaQueries.lg')} {
    display: flex;
    overflow: scroll;
    align-items: center;
  }
  ${themeGet('mediaQueries.xl')} {
    width: 75%;
  }
`

const StyledImage = styled(Image)`
  max-height: 96px;
`

const MAX_WIDTH_PRIMARY_LOGO = 265
const MAX_HEIGHT_PRIMARY_LOGO = 96

const MAX_WIDTH_MEDIA_CARD_BG_IMAGE = 290
const MAX_HEIGHT_MEDIA_CARD_BG_IMAGE = 500

const MAX_WIDTH_MEDIA_CARD_LOGO = 200
const MAX_HEIGHT_MEDIA_CARD_LOGO = 160

export function isMediaImageCard(
  content: MediaImageCard | IMediaSnippet
): content is MediaImageCard {
  return content.contentTypeId === 'mediaImageCard'
}

export default function SpotlightCollection({
  spotlightData
}: {
  spotlightData: SpotlightCollection
}) {
  const {
    primaryLogoImageUrl,
    primaryHeading,
    primaryHeadingColor,
    primarySubheading,
    primarySubheadingColor,
    primaryLogoImageAlt,
    backgroundColor,
    callToAction,
    mediaCards
  } = spotlightData
  const isBrowser = useDetectBrowser()

  const { action, category, label } =
    callToAction?.googleAnalyticsTracking || {}

  if (mediaCards.length === 0) return null

  return (
    <LayoutComponentWrapper>
      <StyledFlex
        flexDirection={['column', null, null, null, 'row']}
        bg={backgroundColor}
        justifyContent="center"
        borderRadius="2xl"
        pt={[0, null, 3]}
        mx={[-3, null, 0]}
        as="section"
      >
        <Flex
          mx={['auto', null, null, null, 0]}
          my="auto"
          mt={['24px', null, null, null, 'auto']}
          maxWidth={['360px', null, null, null, '265px']}
          textAlign="center"
          flexDirection="column"
          style={{ gap: '16px' }}
        >
          {primaryLogoImageUrl && (
            <StyledImage
              m="auto"
              src={`${primaryLogoImageUrl}?fm=webp&q=80&w=${MAX_WIDTH_PRIMARY_LOGO}&h=${MAX_HEIGHT_PRIMARY_LOGO}`}
              alt={primaryLogoImageAlt}
            />
          )}
          {primaryHeading && (
            <Heading
              as="h1"
              textStyle="heading2"
              m={0}
              color={primaryHeadingColor}
            >
              {primaryHeading}
            </Heading>
          )}
          {primarySubheading && (
            <Heading
              as="h2"
              textStyle="paragraph2Bold"
              color={primarySubheadingColor}
            >
              {primarySubheading}
            </Heading>
          )}
          {callToAction?.buttonText && callToAction?.url && (
            <Button
              color={callToAction?.color}
              width={3 / 4}
              m="auto"
              onClick={() => {
                if (action && category && label) {
                  analytics.fireEvent({
                    action,
                    category,
                    label
                  })
                }
                window.open(callToAction.url)
              }}
            >
              {callToAction.buttonText}
            </Button>
          )}
        </Flex>
        <CarouselWrapper minHeight="516px">
          {isBrowser && (
            <Carousel
              arrowsPosition="hide"
              slideSpacing={2}
              visibleSlides={[2.5, 2.5, 3]}
              mobileVisibleSlides={[1.3, 2.5]}
            >
              {mediaCards.map((item, index) => {
                if (isMediaImageCard(item)) {
                  return (
                    <MediaImageCard
                      key={`media-image-card-${index + 1}`}
                      backgroundImageUrl={
                        item.backgroundImageUrl &&
                        `${item.backgroundImageUrl}?fm=webp&q=80&w=${MAX_WIDTH_MEDIA_CARD_BG_IMAGE}&h=${MAX_HEIGHT_MEDIA_CARD_BG_IMAGE}`
                      }
                      callToAction={item.callToAction}
                      contentAlign={item.contentAlign}
                      fixedLogoSize={item.fixedLogoSize}
                      isAlwaysVertical
                      logoImageUrl={
                        item.logoImageUrl &&
                        `${item.logoImageUrl}?fm=webp&q=80&w=${MAX_WIDTH_MEDIA_CARD_LOGO}&h=${MAX_HEIGHT_MEDIA_CARD_LOGO}`
                      }
                      minHeight="500px"
                      showBorder={item.showBorder}
                      showLogoImageBackground={item.showLogoImageBackground}
                      subheading={item.subheading}
                      width="290px"
                    />
                  )
                }

                return (
                  <MediaSnippet key={`media-snippet-${index + 1}`} {...item} />
                )
              })}
            </Carousel>
          )}
        </CarouselWrapper>
      </StyledFlex>
    </LayoutComponentWrapper>
  )
}
